import { Button, Input, NativeSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router";
import {
	Async,
	Badge,
	Empty,
	Field,
	KV,
	PageHead,
	Pager,
	SearchBox,
	Segmented,
	Status,
	useAction,
	useConfirm,
} from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Me, type Plan, type User, type UserDetail } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync, useDebounced, usePaged } from "@/lib/hooks";

export function UsersPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [filter, setFilter] = useState<"" | "admins" | "unverified">("");
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.users({ q: query, filter, limit, offset }),
		(page) => page.users,
		[query, filter],
	);

	return (
		<>
			<PageHead title={t("users.title")} lede={t("users.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<Segmented
					value={filter}
					onChange={setFilter}
					options={[
						{ value: "", label: t("state.all") },
						{ value: "admins", label: t("users.filter.admins") },
						{ value: "unverified", label: t("users.filter.unverified") },
					]}
				/>
			</div>
			<Async state={state}>
				{({ total }) =>
					rows.length === 0 ? (
						<Empty />
					) : (
						<>
							<div className="table-scroll">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("users.email")}</TableHead>
											<TableHead>{t("users.name")}</TableHead>
											<TableHead>{t("state.status")}</TableHead>
											<TableHead>{t("users.providers")}</TableHead>
											<TableHead className="num">{t("users.orgs")}</TableHead>
											<TableHead className="num">{t("users.sessions")}</TableHead>
											<TableHead>{t("state.created")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((u) => (
											<TableRow key={u.id}>
												<TableCell>
													<Link to={`/users/${u.id}`}>{u.email}</Link>
													<span className="sub mono">{u.id}</span>
												</TableCell>
												<TableCell>{u.name ?? "—"}</TableCell>
												<TableCell>
													<div className="chips">
														{u.is_admin ? <Badge tone="accent">{t("users.admin")}</Badge> : null}
														{u.email_verified_at ? (
															<Badge tone="success">{t("users.verified")}</Badge>
														) : (
															<Badge tone="warning">{t("users.unverified")}</Badge>
														)}
													</div>
												</TableCell>
												<TableCell>
													{[u.has_password ? "password" : null, ...u.providers].filter(Boolean).join(", ") || "—"}
												</TableCell>
												<TableCell className="num">{u.orgs}</TableCell>
												<TableCell className="num">{u.sessions}</TableCell>
												<TableCell>{fmt.date(locale, u.created_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<Pager shown={rows.length} total={total} hasMore={hasMore} busy={loadingMore} onMore={more} />
						</>
					)
				}
			</Async>
		</>
	);
}

export function UserPage() {
	const { id = "" } = useParams();
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<UserDetail>(() => api.user(id), [id]);
	const me = useAsync<Me>(() => api.me(), []);
	const plans = useAsync(() => api.plans(), []);
	const confirm = useConfirm();
	const { run, busy } = useAction();

	return (
		<Async state={state}>
			{(d) => {
				const u: User = d.user;
				const isBootstrap = me.data?.bootstrap_email?.toLowerCase() === u.email.toLowerCase();
				const isMe = me.data?.user.user_id === u.id;
				return (
					<>
						<PageHead title={u.email} lede={u.name ?? undefined} crumbs={[{ to: "/users", label: t("users.title") }]}>
							{!u.email_verified_at ? (
								<Button
									variant="default"
									type="button"
									disabled={busy}
									onClick={() =>
										run(
											() => api.verifyUser(u.id),
											() => t("state.saved"),
										).then(state.reload)
									}
								>
									{t("users.verify")}
								</Button>
							) : null}
							<Button
								variant={u.is_admin ? "destructive" : "outline"}
								type="button"
								disabled={busy || isMe || (u.is_admin && isBootstrap)}
								onClick={async () => {
									const ok = await confirm(
										t(u.is_admin ? "users.demote" : "users.promote"),
										t(u.is_admin ? "users.demoteConfirm" : "users.promoteConfirm", { email: u.email }),
										u.is_admin,
									);
									if (ok)
										run(
											() => api.setAdmin(u.id, !u.is_admin),
											() => t("state.saved"),
										).then(state.reload);
								}}
							>
								{t(u.is_admin ? "users.demote" : "users.promote")}
							</Button>
							<Button
								variant="destructive"
								type="button"
								disabled={busy}
								onClick={async () => {
									const ok = await confirm(t("users.revoke"), t("users.revokeConfirm", { email: u.email }), true);
									if (ok)
										run(
											() => api.revokeSessions(u.id),
											(r) => t("users.revoked", { panel: r.panel, console: r.console }),
										).then(state.reload);
								}}
							>
								{t("users.revoke")}
							</Button>
						</PageHead>

						<div className="grid-2">
							<div className="card">
								<KV
									items={[
										[t("state.id"), <span className="mono">{u.id}</span>],
										[
											t("state.status"),
											<div className="chips">
												{u.is_admin ? <Badge tone="accent">{t("users.admin")}</Badge> : null}
												{u.email_verified_at ? (
													<Badge tone="success">{t("users.verified")}</Badge>
												) : (
													<Badge tone="warning">{t("users.unverified")}</Badge>
												)}
												{isBootstrap ? <Badge tone="outline">{t("users.bootstrap")}</Badge> : null}
											</div>,
										],
										[t("users.password"), t(u.has_password ? "state.yes" : "state.no")],
										[t("nav.language"), u.locale],
										[t("state.created"), fmt.dateTime(locale, u.created_at)],
										[t("users.sessions"), `${u.sessions} · ${t("users.consoleSessions", { n: d.admin_sessions })}`],
									]}
								/>
							</div>

							<PasswordCard user={u} bootstrap={isBootstrap} onDone={state.reload} />

							<div className="card">
								<h2>{t("users.orgs")}</h2>
								{d.orgs.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>{t("orgs.name")}</TableHead>
												<TableHead>{t("orgs.role")}</TableHead>
												<TableHead>{t("orgs.plan")}</TableHead>
												<TableHead className="num">{t("orgs.credits")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{d.orgs.map((o) => (
												<TableRow key={o.id}>
													<TableCell>
														<Link to={`/orgs/${o.id}`}>{o.name}</Link>
														{o.paused ? <Badge tone="warning">{t("orgs.paused")}</Badge> : null}
													</TableCell>
													<TableCell>{o.role}</TableCell>
													<TableCell>
														{o.plan_key}
														{o.subscription_status ? (
															<span className="sub">
																<Status value={o.subscription_status} prefix="billing.status" />{" "}
																{fmt.date(locale, o.subscription_ends_at)}
															</span>
														) : null}
													</TableCell>
													<TableCell className="num">{fmt.number(locale, o.credits)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>

							<CreditsCard detail={d} onDone={state.reload} />
							<LevelCard detail={d} plans={plans.data?.plans ?? []} onDone={state.reload} />

							<div className="card">
								<h2>{t("users.identities")}</h2>
								{d.identities.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<KV
										items={d.identities.map((i) => [
											i.provider,
											`${i.email ?? "—"} · ${fmt.date(locale, i.created_at)}`,
										])}
									/>
								)}
							</div>
							<div className="card">
								<h2>{t("users.actions")}</h2>
								{d.actions.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table>
										<TableBody>
											{d.actions.map((a) => (
												<TableRow key={a.id}>
													<TableCell className="mono">{a.action}</TableCell>
													<TableCell className="mono">{a.target ?? "—"}</TableCell>
													<TableCell>{fmt.dateTime(locale, a.created_at)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
						</div>
					</>
				);
			}}
		</Async>
	);
}

/** Twenty characters from the browser's own randomness, no ambiguous glyphs. */
function generatePassword(): string {
	const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
	const bytes = new Uint8Array(20);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

function PasswordCard({ user, bootstrap, onDone }: { user: User; bootstrap: boolean; onDone: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const [password, setPassword] = useState("");

	async function submit(e: FormEvent) {
		e.preventDefault();
		const ok = await confirm(t("users.resetPassword"), user.email, true);
		if (!ok) return;
		const r = await run(
			() => api.setPassword(user.id, password),
			(r) => t("users.passwordSet", { n: r.sessions_ended }),
		);
		if (r) onDone();
	}

	return (
		<form className="card form" onSubmit={submit}>
			<h2>{t("users.resetPassword")}</h2>
			<p className="hint">{bootstrap ? t("users.bootstrapPassword") : t("users.resetPasswordHint")}</p>
			<Field label={t("users.newPassword")}>
				<div className="row">
					<Input
						type="text"
						autoComplete="off"
						spellCheck={false}
						minLength={8}
						required
						disabled={bootstrap}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						style={{ flex: 1, fontFamily: "var(--font-mono)" }}
					/>
					<Button variant="default" type="button" disabled={bootstrap} onClick={() => setPassword(generatePassword())}>
						{t("users.generate")}
					</Button>
				</div>
			</Field>
			<div className="form-foot">
				<Button variant="destructive" type="submit" disabled={busy || bootstrap || password.length < 8}>
					{t("users.resetPassword")}
				</Button>
			</div>
		</form>
	);
}

function CreditsCard({ detail, onDone }: { detail: UserDetail; onDone: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const [org, setOrg] = useState(detail.orgs[0]?.id ?? "");
	const [amount, setAmount] = useState("1000");
	const [note, setNote] = useState("");

	async function submit(e: FormEvent) {
		e.preventDefault();
		const n = Number(amount);
		if (!org || !Number.isFinite(n) || n === 0) return;
		if (n < 0) {
			const name = detail.orgs.find((o) => o.id === org)?.name ?? org;
			const ok = await confirm(
				t("orgs.revoke"),
				t("orgs.revokeConfirm", { credits: fmt.number(locale, -n), name }),
				true,
			);
			if (!ok) return;
		}
		const r = await run(
			() =>
				n > 0
					? api.grant(org, { credits: n, note: note || undefined, expires_at: null })
					: api.revoke(org, { credits: -n, note: note || undefined }),
			(r) => t(n > 0 ? "orgs.granted" : "orgs.revoked", { balance: fmt.number(locale, r.balance) }),
		);
		if (r) onDone();
	}

	return (
		<form className="card form" onSubmit={submit}>
			<h2>{t("users.credits")}</h2>
			<p className="hint">{t("users.creditsHint")}</p>
			{detail.orgs.length === 0 ? (
				<p className="muted">{t("users.noOrgs")}</p>
			) : (
				<>
					<div className="field-row">
						<Field label={t("users.forAccount")}>
							<NativeSelect value={org} onChange={(e) => setOrg(e.target.value)}>
								{detail.orgs.map((o) => (
									<option key={o.id} value={o.id}>
										{o.name} · {fmt.number(locale, o.credits)}
									</option>
								))}
							</NativeSelect>
						</Field>
						<Field label={t("orgs.grantAmount")}>
							<Input type="number" step={1} required value={amount} onChange={(e) => setAmount(e.target.value)} />
						</Field>
					</div>
					<Field label={t("state.note")} hint={t("state.noteHint")}>
						<Input required value={note} onChange={(e) => setNote(e.target.value)} />
					</Field>
					<div className="form-foot">
						<Button
							variant={Number(amount) < 0 ? "destructive" : "default"}
							type="submit"
							disabled={busy || !org || Number(amount) === 0}
						>
							{t("users.creditsApply")}
						</Button>
					</div>
				</>
			)}
		</form>
	);
}

function LevelCard({ detail, plans, onDone }: { detail: UserDetail; plans: Plan[]; onDone: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const active = plans.filter((p) => p.active);
	const [org, setOrg] = useState(detail.orgs[0]?.id ?? "");
	const [plan, setPlan] = useState(detail.orgs[0]?.plan_key ?? "");
	const [months, setMonths] = useState("1");
	const [note, setNote] = useState("");
	const paid = active.find((p) => p.key === plan)?.price_usd_micros ?? 0;

	async function submit(e: FormEvent) {
		e.preventDefault();
		if (!org || !plan) return;
		const ok = await confirm(t("orgs.comp"), t("orgs.compHint"));
		if (!ok) return;
		const r = await run(
			() => api.setPlan(org, { plan_key: plan, months: Number(months), note: note || undefined }),
			(r) =>
				r.period_end
					? t("orgs.comped", { plan, until: fmt.date(locale, r.period_end), credits: r.credits_granted })
					: t("orgs.planSet", { plan }),
		);
		if (r) onDone();
	}

	return (
		<form className="card form" onSubmit={submit}>
			<h2>{t("users.level")}</h2>
			<p className="hint">{t("orgs.compHint")}</p>
			{detail.orgs.length === 0 ? (
				<p className="muted">{t("users.noOrgs")}</p>
			) : (
				<>
					<div className="field-row">
						<Field label={t("users.forAccount")}>
							<NativeSelect
								value={org}
								onChange={(e) => {
									setOrg(e.target.value);
									setPlan(detail.orgs.find((o) => o.id === e.target.value)?.plan_key ?? plan);
								}}
							>
								{detail.orgs.map((o) => (
									<option key={o.id} value={o.id}>
										{o.name} · {o.plan_key}
									</option>
								))}
							</NativeSelect>
						</Field>
						<Field label={t("orgs.plan")}>
							<NativeSelect value={plan} onChange={(e) => setPlan(e.target.value)}>
								{active.map((p) => (
									<option key={p.key} value={p.key}>
										{locale === "zh" ? p.name_zh : p.name_en} ({p.key})
									</option>
								))}
							</NativeSelect>
						</Field>
						<Field label={t("orgs.compMonths")}>
							<Input
								type="number"
								min={1}
								max={36}
								value={months}
								disabled={paid <= 0}
								onChange={(e) => setMonths(e.target.value)}
							/>
						</Field>
					</div>
					<Field label={t("state.note")}>
						<Input value={note} onChange={(e) => setNote(e.target.value)} />
					</Field>
					<div className="form-foot">
						<Button variant="default" type="submit" disabled={busy || !org || !plan || active.length === 0}>
							{t("orgs.comp")}
						</Button>
					</div>
				</>
			)}
		</form>
	);
}
